import {
  LOGO_GREY,
  LOGO_LIGHT_BLUE,
  LOGO_MASTER,
  LOGO_MEDIUM_DARK_BLUE,
  LOGO_YELLOW,
} from "core/consts";
import { LogoColors } from "core/types";
import { MEDIUM, SMALL, dp, sizing } from "ds_legacy/materials/metrics";
import Image from "dsl/atoms/Image";
import React, { CSSProperties, Suspense } from "react";
import { LinkV2Props } from "../LinkV2";

export type RecareLogoWithNameProps = {
  alt?: string;
  linkProps?: LinkV2Props;
  shouldSkipLazyLoad?: boolean;
  size?: string;
  style?: CSSProperties;
  variant?: LogoColors;
};

export type RecareLogoOnlyProps = {
  style?: CSSProperties;
  variant?: Omit<LogoColors, "medium-dark-blue">;
};

export function getRecareLogoWithNamePath(
  variant: RecareLogoWithNameProps["variant"],
  isPredict?: boolean,
) {
  switch (variant) {
    case LOGO_GREY:
      return "logo/svg/recare_logo_rgb_greyscale.svg";
    case LOGO_MASTER:
      return isPredict
        ? "logo/svg/recare_predict_logo_rgb_mediumdarkblue_white.svg"
        : "logo/svg/recare_logo_rgb_master.svg";
    case LOGO_LIGHT_BLUE:
      return "logo/svg/recare_logo_rgb_lightblue_bg.svg";
    case LOGO_MEDIUM_DARK_BLUE:
      return "logo/svg/recare_logo_rgb_mediumdarkblue_bg.svg";
    case LOGO_YELLOW:
      return "logo/svg/recare_admissions_logo_rgb_yellow.svg";
    default:
      return isPredict
        ? "logo/svg/recare_predict_logo_rgb_yellow_mediumdarkblue.svg"
        : "logo/svg/recare_logo_rgb_white.svg";
  }
}

export function getRecareLogoOnlyPath(
  variant?: RecareLogoOnlyProps["variant"],
) {
  switch (variant) {
    case LOGO_GREY:
      return "logo/icon-only/regular-usage/recare_picture-mark_rgb_greyscale.svg";
    case LOGO_MASTER:
      return "logo/icon-only/regular-usage/recare_picture-mark_rgb_master.svg";
    case LOGO_LIGHT_BLUE:
      return "logo/icon-only/regular-usage/recare_picture-mark_rgb_lightblue_bg.svg";
    default:
      return "logo/icon-only/regular-usage/recare_picture-mark_rgb_white.svg";
  }
}

export const getLogoSize = ({ size }: { size?: string }) => {
  switch (size) {
    case SMALL:
      return {
        height: dp(27),
        width: "auto",
      };

    case MEDIUM:
      return {
        height: sizing(4),
        width: "auto",
      };

    default:
      return {
        height: dp(43),
        width: "auto",
      };
  }
};

function Placeholder(props: Partial<RecareLogoWithNameProps>) {
  return (
    <div
      style={{
        ...(props?.style ?? {}),
        ...(props?.size ? getLogoSize({ size: props.size }) : {}),
      }}
    />
  );
}

function Suspend({
  children,
  shouldSkip,
}: {
  children: React.ReactNode;
  shouldSkip?: boolean;
}) {
  if (shouldSkip) return <>{children}</>;

  return (
    <Suspense
      fallback={
        <Placeholder
          {...(React.isValidElement(children) ? (children.props as any) : {})}
        />
      }
    >
      {children}
    </Suspense>
  );
}

export function RecareNameLogo({
  alt,
  linkProps,
  shouldSkipLazyLoad,
  size,
  style,
  variant,
}: RecareLogoWithNameProps) {
  const { height, width } = getLogoSize({ size });

  return (
    <Suspend shouldSkip={shouldSkipLazyLoad}>
      <Image
        alt={alt ?? "recare-logo"}
        linkProps={linkProps}
        path={getRecareLogoWithNamePath(variant)}
        style={{
          ...style,
          height,
          maxHeight: height,
          minHeight: height,
          objectFit: "contain",
          width,
        }}
        testId="recare-logo"
      />
    </Suspend>
  );
}

export function RecareLogoOnly({
  style = { height: dp(32) },
  variant,
}: RecareLogoOnlyProps) {
  return (
    <Suspend>
      <Image
        alt="Logo Recare"
        path={getRecareLogoOnlyPath(variant)}
        style={{
          ...style,
        }}
        testId="recare-logo"
      />
    </Suspend>
  );
}
